import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListFiles,
  useDeleteFile,
  getListFilesQueryKey,
  generatePresignedUrl,
} from "@packages/api/madlenAPI";
import { TeacherDocumentResponseDto } from "@packages/api/madlenAPI.schemas";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Button } from "@core/button";

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const MyFilesView = () => {
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);

  const listFilesQueryKey = getListFilesQueryKey();
  const { data: uploadedFiles } = useListFiles();

  const deleteFile = useDeleteFile({
    mutation: {
      onMutate: (data) => {
        setIsDeleting(data.id);
      },
      onSettled: () => {
        setIsDeleting(undefined);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: listFilesQueryKey });
      },
    },
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const formData = new FormData();
    formData.append("file", acceptedFiles[0], acceptedFiles[0].name);

    setIsUploading(true);

    const response = await generatePresignedUrl({
      fileName: acceptedFiles[0].name,
    });

    const signedUrl = response.presignedUrl;

    if (!signedUrl) {
      setUploadError(
        "Dosya yüklenirken bir hata oluştu. Lütfen tekrar deneyin.",
      );
      setIsUploading(false);
      return;
    }

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      body: acceptedFiles[0],
    });

    console.log(uploadResponse);

    queryClient.invalidateQueries({ queryKey: listFilesQueryKey });
    setIsUploading(false);
    return response;
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "text/plain": [".txt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
    },
    maxSize: 15 * 1024 * 1024, // 15MB
  });

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const closeModal = () => {
    setSelectedPdf(null);
    setPageNumber(1);
    setScale(1.0);
  };

  return (
    <div className="container mx-auto px-4 py-4">
      <h1 className="text-2xl font-bold mb-8">Dosyalarım</h1>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 mb-8 text-center hover:border-blue-500 hover:bg-blue-50 cursor-pointer hover:dark:border-blue-800 hover:dark:bg-blue-950 ${isDragActive ? "border-blue-500 bg-blue-50 dark:border-blue-800 dark:bg-blue-950" : "border-gray-300 dark:border-gray-500"} ${isUploading ? "opacity-50 pointer-events-none" : ""}`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center">
          <svg
            className="h-12 w-12 text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          {isDragActive ? (
            <p>Dosyayı buraya bırakın...</p>
          ) : (
            <>
              <p>Dosyaları buraya sürükleyin veya seçmek için tıklayın</p>
              <p className="text-sm text-gray-500 mt-2">
                PDF, PPTX, DOCX, TXT (Max 15MB)
              </p>
            </>
          )}
        </div>
      </div>

      {uploadError && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-8">
          {uploadError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {uploadedFiles?.data?.map((file: TeacherDocumentResponseDto) => (
          <div
            key={file.id}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden ${isDeleting === file.id ? "opacity-50" : ""}`}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-8 w-8 text-gray-400 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs">
                      {file.fileName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(file.uploadedAt!).toLocaleDateString("tr-TR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
                <Button
                  variant="solid"
                  size="icon"
                  rounded="md"
                  textColor="text-red-500 hover:text-red-700"
                  disabled={isDeleting === file.id}
                  onClick={() => deleteFile.mutateAsync({ id: file.id! })}
                  leftIcon={
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  }
                />
              </div>
              <div className="flex space-x-2">
                {file.fileName?.toLowerCase().endsWith(".pdf") && (
                  <Button
                    variant="solid"
                    size="xs"
                    rounded="lg"
                    textColor="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={() => setSelectedPdf(file.url!)}
                    rightIcon={
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    }
                  >
                    <span>Önizle</span>
                  </Button>
                )}
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <span>İndir</span>
                  <svg
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PDF Preview Modal */}
      {selectedPdf && (
        <div
          className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="xs"
                  rounded="md"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                  disabled={pageNumber <= 1}
                >
                  ←
                </Button>
                <span className="text-sm">
                  Sayfa {pageNumber} / {numPages}
                </span>
                <Button
                  variant="ghost"
                  size="md"
                  rounded="md"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() =>
                    setPageNumber(Math.min(numPages!, pageNumber + 1))
                  }
                  disabled={pageNumber >= numPages!}
                >
                  →
                </Button>

                <Button
                  variant="ghost"
                  size="xs"
                  rounded="md"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setScale(scale + 0.2)}
                >
                  +
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  rounded="md"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setScale(Math.max(0.4, scale - 0.2))}
                >
                  -
                </Button>
              </div>
              <Button
                onClick={closeModal}
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                rounded="md"
                size="icon"
              >
                ✕
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="flex justify-center">
                <Document
                  file={selectedPdf}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  }
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyFilesView;
